<script>
/**
 * UiPaginator
 *
 * Corrige issue de paginador con v-model y router, 
 * (al montal el componente arrojaba un ajuste al $router o el evento refresh, en lugar de simplemente cargar la configuración de value en WorkingValue)
 * 
 * @version 1.5
 * @author Daniel Lepe
 * @date 16/dic/2025
 * 
 * Permite la visualización de páginador, puede funcionar diractamente con v-model o con $route.
 * Revisa que la página exista únicamente para paginado normal, para paginado por router, ignora la validación.
 *
 * @verision 1.4
 * @author Daniel Lepe
 * @date 28/nov/2022
 *
 *
 * Páginas remueve el fondo rojo
 *
 * @verision 1.3
 * @author Daniel Lepe
 * @date 28/nov/2022
 *
 * Páginas centradas en el paginador
 *
 * @verision 1.2
 * @author Daniel Lepe
 * @date 28/nov/2022
 *
 * Corrige la precarga de página activa cuando no es modo="normal".
 *
 * @verision 1.1
 * @author Daniel Lepe
 * @date 23/nov/2022
 *
 * Params:
 *
 * v-model: Type: NUMBER
 *  Página activa
 *
 * itemsLength: Type: Number
 *  Requerido, Items en la lista.
 *
 * pageSize: Type: Number
 *  Elementos por página.
 *  Por defecto: 10
 *
 * mode: [normal|router]
 *  Por defecto: "Normal"
 *  Establece si actualizará directamente desde $router.[routeParam]
 *
 * routeParam: Type: String
 *  Por defect: "page"
 *  Cuando mode == "router", tomará el parámtro de routeParam en $route.params para actualizar v-model y automáticamente redireccionar
 *      a dicha página con $router.push()
 *
 *  Muestra de paginado de colección
 *
 *  // INIT
 *  let collection = this.productsUnpaginated,
 *      page = this.page,
 *      pageSize = this.pageSize;
 *
 *  // RETURNS PAGINATED COLLECITON
 *  return _.slice(collection, (page - 1) * pageSize, (page) * pageSize)
 *
 * @version 1.0
 * @author Daniel Lepe
 * @date 22/nov/2022
 */
import _ from "lodash";

export default {
	props: {
		paginationSize: {
			type: [Number, String],
			default: 8,
		},
		value: {
			type: [Number, String],
			default: 1,
		},
		itemsLength: {
			type: Number,
			default: 0,
		},
		pageSize: {
			type: [Number, String],
			default: 3,
		},
		routeAttr: {
			type: String,
			default: "query", // [query | params]
		},
		mode: {
			type: String,
			default: "normal", // [normal|router]
		},
		routeParam: {
			type: String,
			default: "page", // [page|pagina|...]
		},
		pagesLabel: {
			type: String,
			default: "página(s)",
		},
		perPageLabel: {
			type: String,
			default: "Por página.",
		},
		totalize: {
			type: Boolean,
			default: true,
		},
		totalizeConcept: {
			type: String,
			default: "elemento(s) en total",
		},
	},
	computed: {
		pages() {
			let pages = [];
			let i = 1;
			do {
				pages.push(i);
				i++;
			} while (i <= Math.ceil(this.itemsLength / this.pageSize));
			return pages;
		},
	},
	data: () => {
		return {
			workingValue: 1,
		};
	},
	methods: {
		setPage(page) {
			// REVIEW IF PAGE IS READY
			if (this.pages.indexOf(page) == -1 && this.mode == "normal") {
				console.debug("Page not available...");
				this.workingValue = 1;
			} else if (parseInt(this.workingValue) != parseInt(page)) {
				// Working value update
				this.workingValue = page;
				this.$emit("input", this.workingValue);

				// Emisión de evento de refresh
				if (this.mode == "normal") {
					this.$emit("refresh", this.workingValue);
				} else if((this.mode == "router")) {
					let newRoute = {
						name: this.$route.name, 
						[this.routeAttr]: _.cloneDeep(this.$route[this.routeAttr])
					};
					newRoute[this.routeAttr][this.routeParam] = this.workingValue;
					console.debug("Navigating to new route with updated page param...", newRoute);
					this.$router.replace(newRoute).catch(() => {
						this.$emit("input", this.workingValue);
					});
				}
			}
		},
		setPageAndScroll(page) {
			console.log('Navigating to.setPageAndScroll', page, this.workingValue)
			if (parseInt(page) == parseInt(this.workingValue))
				return;
			this.setPage(page);
			setTimeout(() => {
				document.getElementById("app").scrollTop = 0;
			}, 900);
		},
	},
	watch: {
		value() {
			// if (this.value != this.workingValue) this.setPage(this.value);
		}
	},
	mounted() {
		if ((this.mode == "router") && (!!this.$route[this.routeAttr][this.routeParam]) && (this.$route[this.routeAttr][this.routeParam] != this.workingValue)) {
			this.workingValue = _.clone(this.$route[this.routeAttr][this.routeParam]);
		} else if (this.value != 1) {
			this.workingValue = _.clone(this.value);
		}
	},
};
</script>

<template>
	<div class="ui-paginator" :class="{ hasTotalize: totalize }">

		<b-pagination v-model="workingValue" align="center" :limit="paginationSize" :total-rows="itemsLength"
			:per-page="pageSize" @change="setPageAndScroll" />

		<div class="totalize" v-if="totalize">
			{{ itemsLength }} {{ totalizeConcept }} / {{ pages.length }}
			{{ pagesLabel }}
			<br />
			<small> ({{ pageSize }} {{ perPageLabel }}) </small>
		</div>

	</div>
</template>

<style lang="less">
.ui-paginator {
	padding: 0 !important;
	position: relative;

	.page-item.active .page-link {
		z-index: 3;
		color: #fff;
		background-color: var(--primary);
		border-color: var(--primary);
	}

	.page-link {
		// color: #007bff;
		color: var(--primary);
	}

	.overfl-holder {
		width: auto;
		max-width: 100%;
		// display: inline;
		text-align: center;
		background-color: #f4f4f4;
		border: var(--border-style);
		border-left: none;
		border-right: none;

		.overfl {
			// background: red;
			padding: 0.5em !important;
			display: inline-flex;
			flex-flow: row;
			gap: 0.5em;
			max-width: 100%;
			overflow-x: auto;

			.page {
				padding: 0.5em 1.5em;
				position: relative;
				border-radius: 0.5em;
				font-weight: bolder;
				white-space: nowrap;
				transition: all 450ms ease-in;
				overflow: clip;

				.activeOnly {
					display: none;
				}

				DIV {
					// background: red;
					position: absolute;
					top: 0;
					left: 0;
					right: 0;
					bottom: 0;
					width: 100%;
				}

				&.active {
					color: white;
					background-color: var(--primary);

					.activeOnly {
						display: inline;
					}
				}
			}
		}
	}

	&.hasTotalize {
		.totalize {
			position: static;
			bottom: 0;
			left: 0;
			right: 0;
			z-index: 10;
			text-align: center;
			color: var(--primary);
			font-size: 0.75em;
			text-transform: uppercase;
			padding: 0.25em;
		}
	}
}
</style>